import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";

// The map's layer panel is position:fixed above the dialog stack, so a wide
// dialog would otherwise have it floating over its right edge. Dialogs that
// need the full width register themselves here while open and the panel
// hides, the same way it does for the report modal.
interface LayerPanelContextValue {
  hiddenCount: number;
  adjust: (delta: number) => void;
}

const LayerPanelContext = createContext<LayerPanelContextValue | undefined>(undefined);

export function LayerPanelProvider({ children }: { children: ReactNode }) {
  const [hiddenCount, setHiddenCount] = useState(0);
  const adjust = useCallback((delta: number) => setHiddenCount((n) => Math.max(0, n + delta)), []);
  return <LayerPanelContext.Provider value={{ hiddenCount, adjust }}>{children}</LayerPanelContext.Provider>;
}

/** True while at least one full-width dialog has asked for the panel to hide. */
export const useLayerPanelHidden = (): boolean => (useContext(LayerPanelContext)?.hiddenCount ?? 0) > 0;

/** Hide the layer panel while `active`. Safe to call outside the provider (no-op). */
export const useHideLayerPanel = (active: boolean) => {
  const adjust = useContext(LayerPanelContext)?.adjust;
  useEffect(() => {
    if (!active || !adjust) return;
    adjust(1);
    return () => adjust(-1);
  }, [active, adjust]);
};
